import { html } from 'lit-html';
import { api, get } from '../api.js';
import { act, app, confirmDialog, emptyRow, helpLink, isAdmin, type Page } from '../app.js';
import { when } from '../format.js';
import { hoursEditor, hoursSummary, type Hours } from './rules.js';

type Mode = 'off' | 'dry_run' | 'enforce';
type Scope = 'agent_queue' | 'agent';

interface QueuePolicy {
  queueId: string;
  queueName: string | null;
  mode: Mode;
  graceThreshold: number | null;
  graceWindowDays: number | null;
  graceScope: Scope | null;
  enforcementHours: Hours | null;
  notes: string | null;
  updatedBy: string;
  updatedAt: string;
}

interface GlobalDefaults {
  graceThreshold: number;
  graceWindowDays: number;
  graceScope: Scope;
  enforcementHours: Hours | null;
}

const MODES: [Mode, string][] = [
  ['off', 'Off'],
  ['dry_run', 'Dry run'],
  ['enforce', 'Enforce'],
];
const SCOPE_LABEL: Record<Scope, string> = { agent_queue: 'per agent and queue', agent: 'per agent, all queues' };

let queues: QueuePolicy[] = [];
let globals: GlobalDefaults | null = null;
let matches: { id: string; name: string | null }[] = [];
let searched = '';
let editing: string | null = null;
let draft: Pick<QueuePolicy, 'graceThreshold' | 'graceWindowDays' | 'graceScope' | 'enforcementHours' | 'notes'> | null = null;

const label = (q: QueuePolicy) => q.queueName ?? q.queueId;

async function setMode(q: QueuePolicy, mode: Mode) {
  if (mode === q.mode) return;
  if (mode === 'enforce') {
    const c = await confirmDialog({
      title: `Enforce on ${label(q)}?`,
      body: 'Agents who miss ACW on this queue will be deactivated on it, if the global settings allow live enforcement. They reactivate it themselves in Genesys.',
      confirmLabel: 'Enforce',
    });
    if (!c.ok) return app.rerender();
  }
  await act(() => api('PUT', `/queues/${encodeURIComponent(q.queueId)}`, { mode }), `${label(q)}: ${mode.replace('_', ' ')}`, queuesPage.load);
}

async function search(e: Event) {
  e.preventDefault();
  const name = String(new FormData(e.target as HTMLFormElement).get('name') ?? '').trim();
  if (!name) return;
  searched = name;
  try {
    matches = (await get<{ matches: { id: string; name: string | null }[] }>(`/queues/lookup?name=${encodeURIComponent(name)}`)).matches;
  } catch (err) {
    matches = [];
    searched = `${name} (lookup failed: ${(err as Error).message})`;
  }
  app.rerender();
}

async function add(m: { id: string; name: string | null }) {
  const ok = await act(() => api('PUT', `/queues/${encodeURIComponent(m.id)}`, { mode: 'dry_run' }), `${m.name ?? m.id} added in dry run`, queuesPage.load);
  if (ok) {
    matches = [];
    searched = '';
    app.rerender();
  }
}

async function remove(q: QueuePolicy) {
  const c = await confirmDialog({
    title: `Stop managing ${label(q)}?`,
    body: 'Timeouts on this queue will be recorded as "queue not enforced". Agents already deactivated stay deactivated until they reactivate.',
    confirmLabel: 'Remove',
    tone: 'danger',
  });
  if (c.ok) await act(() => api('DELETE', `/queues/${encodeURIComponent(q.queueId)}`), 'Queue removed', queuesPage.load);
}

function startEdit(q: QueuePolicy) {
  editing = editing === q.queueId ? null : q.queueId;
  draft = editing
    ? { graceThreshold: q.graceThreshold, graceWindowDays: q.graceWindowDays, graceScope: q.graceScope, enforcementHours: q.enforcementHours, notes: q.notes }
    : null;
  app.rerender();
}

async function saveOverrides(q: QueuePolicy) {
  const d = draft!;
  const ok = await act(
    () =>
      api('PUT', `/queues/${encodeURIComponent(q.queueId)}`, {
        mode: q.mode,
        graceThreshold: d.graceThreshold,
        graceWindowDays: d.graceWindowDays,
        graceScope: d.graceScope,
        enforcementHours: d.enforcementHours,
        notes: d.notes && d.notes.trim() ? d.notes.trim() : null,
      }),
    `${label(q)}: overrides saved`,
    queuesPage.load,
  );
  if (ok) {
    editing = null;
    draft = null;
    app.rerender();
  }
}

const graceText = (t: number | null, w: number | null, sc: Scope | null) => {
  const g = globals;
  const threshold = t ?? g?.graceThreshold ?? 1;
  if (threshold <= 1) return 'every timeout';
  return `${threshold} in ${w ?? g?.graceWindowDays ?? 7}d, ${SCOPE_LABEL[sc ?? g?.graceScope ?? 'agent_queue']}`;
};

function overridesEditor(q: QueuePolicy) {
  const d = draft!;
  const set = (patch: Partial<typeof d>) => {
    draft = { ...d, ...patch };
    app.rerender();
  };
  const numOrNull = (v: string) => (v.trim() === '' ? null : Math.max(1, Math.round(Number(v))));
  return html`<tr class="detail"><td colspan="6">
    <div class="overrides">
      <div>
        <h3>Grace count for this queue</h3>
        <p class="hint">Leave blank to use the global rule (${graceText(null, null, null)}).</p>
        <p class="compact">Deactivate on timeout number
          <input type="number" min="1" max="100" placeholder=${String(globals?.graceThreshold ?? 1)} .value=${d.graceThreshold === null ? '' : String(d.graceThreshold)}
            @change=${(e: Event) => set({ graceThreshold: numOrNull((e.target as HTMLInputElement).value) })}>
          within <input type="number" min="1" max="365" placeholder=${String(globals?.graceWindowDays ?? 7)} .value=${d.graceWindowDays === null ? '' : String(d.graceWindowDays)}
            @change=${(e: Event) => set({ graceWindowDays: numOrNull((e.target as HTMLInputElement).value) })}> days, counted
          <select @change=${(e: Event) => set({ graceScope: ((e.target as HTMLSelectElement).value || null) as Scope | null })}>
            <option value="" ?selected=${d.graceScope === null}>as the global rule</option>
            <option value="agent_queue" ?selected=${d.graceScope === 'agent_queue'}>per agent and queue</option>
            <option value="agent" ?selected=${d.graceScope === 'agent'}>per agent, all queues</option>
          </select></p>
      </div>
      <div>
        <h3>Enforcement hours for this queue</h3>
        ${hoursEditor(d.enforcementHours, (next) => set({ enforcementHours: next }), true, {
          none: html`<p>Uses the global hours: <b>${hoursSummary(globals?.enforcementHours ?? null)}</b>.</p>`,
          setLabel: 'Set hours for this queue',
          clearLabel: 'Use the global hours',
        })}
      </div>
      <div>
        <h3>Notes</h3>
        <textarea rows="2" maxlength="1000" placeholder="Optional, e.g. why this queue is in dry run" .value=${d.notes ?? ''}
          @input=${(e: Event) => { draft = { ...d, notes: (e.target as HTMLTextAreaElement).value }; }}></textarea>
      </div>
      <div class="actions">
        <button class="btn quiet" @click=${() => startEdit(q)}>Cancel</button>
        <button class="btn primary" @click=${() => saveOverrides(q)}>Save overrides</button>
      </div>
    </div>
  </td></tr>`;
}

export const queuesPage: Page = {
  title: 'Queues',
  async load() {
    const [q, s] = await Promise.all([get<{ queues: QueuePolicy[] }>('/queues'), get<{ global: GlobalDefaults }>('/settings')]);
    queues = q.queues;
    globals = s.global;
  },
  view() {
    const admin = isAdmin();
    const listed = new Set(queues.map((q) => q.queueId));
    const cols = admin ? 6 : 5;
    return html`
      <h1>Queues ${helpLink('queues')}</h1>
      <p class="hint">Only queues listed here are managed. <b>Dry run</b> records what would happen; <b>Enforce</b> deactivates agents who time out on that queue. New queues start in dry run.</p>
      <div class="table-wrap"><table>
        <thead><tr><th>Queue</th><th>Mode</th><th>Grace</th><th>Hours</th><th>Last changed</th>${admin ? html`<th></th>` : ''}</tr></thead>
        <tbody>${queues.length
          ? queues
              .slice()
              .sort((a, b) => (a.queueName ?? '').localeCompare(b.queueName ?? ''))
              .map((q) => html`<tr class=${editing === q.queueId ? 'selected' : ''}>
                <td><b>${q.queueName ?? '(unnamed)'}</b><br><code class="id">${q.queueId}</code>${q.notes ? html`<br><span class="muted">${q.notes}</span>` : ''}</td>
                <td>${admin
                  ? html`<div class="pill-group" role="group" aria-label="Mode for ${label(q)}">${MODES.map(
                      ([m, l]) => html`<button type="button" class="pill-btn ${m} ${q.mode === m ? 'active' : ''}" aria-pressed=${q.mode === m ? 'true' : 'false'}
                        @click=${() => setMode(q, m)}>${l}</button>`,
                    )}</div>`
                  : html`<span class="pill ${q.mode === 'enforce' ? 'accent' : q.mode === 'dry_run' ? 'info' : 'neutral'}">${MODES.find(([m]) => m === q.mode)?.[1]}</span>`}</td>
                <td>${q.graceThreshold || q.graceWindowDays || q.graceScope ? graceText(q.graceThreshold, q.graceWindowDays, q.graceScope) : html`<span class="muted">global</span>`}</td>
                <td>${q.enforcementHours ? hoursSummary(q.enforcementHours) : html`<span class="muted">global</span>`}</td>
                <td class="nowrap">${when(q.updatedAt)}<br><span class="muted">${q.updatedBy.replace(/^sso:/, '')}</span></td>
                ${admin ? html`<td class="nowrap"><button class="btn quiet compact" @click=${() => startEdit(q)}>${editing === q.queueId ? 'Close' : 'Overrides'}</button>
                  <button class="btn destructive outline compact" @click=${() => remove(q)}>Remove</button></td>` : ''}
              </tr>
              ${admin && editing === q.queueId ? overridesEditor(q) : ''}`)
          : emptyRow(cols, 'No queues are managed yet.')}</tbody>
      </table></div>

      ${admin ? html`
        <section class="panel">
          <h2>Add a queue</h2>
          <form class="inline" @submit=${search}>
            <input name="name" placeholder="Search Genesys queues by name, e.g. Medicare" required>
            <button class="btn">Search</button>
          </form>
          ${searched ? html`
            <div class="table-wrap"><table>
              <thead><tr><th>Genesys queue</th><th>ID</th><th></th></tr></thead>
              <tbody>${matches.length
                ? matches.map((m) => html`<tr><td>${m.name}</td><td><code class="id">${m.id}</code></td>
                    <td>${listed.has(m.id) ? html`<span class="muted">already listed</span>` : html`<button class="btn compact" @click=${() => add(m)}>Add in dry run</button>`}</td></tr>`)
                : emptyRow(3, `No queues found for "${searched}".`)}</tbody>
            </table></div>` : ''}
        </section>` : ''}`;
  },
};
